// @ai-generated 2026-07-10T00:00:00Z by matthias.gessinger@synqony.com using Claude Sonnet 5
import { useIsPortalSession } from '@Contexts/session';
import { getShortUnitLabel } from '@Model';
import { type ApiRequisition, type ApiRequisitionPosition, type ApiService } from '@Model';
import {
    type AsyncStateUpdate,
    type Awaitable,
    Button,
    FieldWithError,
    FlexColumn,
    FloatingCard,
    Grid,
    GridColumns,
    iconSizeMd,
    iconSizeSm,
    isLinked,
    isOk,
    NumberInput,
    RichSelect,
    type RichSelectItemPreview,
    useEditSubObject,
    useGuiBinder,
    useLinkFieldApi,
    useProp,
    useTranslate,
    useWizardErrors,
    WizardStepTitle,
} from '@synqo/frontend-component-library';
import { formatMaterialName } from '@utils/requisitionDisplay';
import { sortByDisplayString } from '@utils/sortByDisplayString';
import { IconPlus, IconTrash } from '@tabler/icons-react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { type AvvCodes, useAvvCodes } from '../useAvvCodes';
import { materialErrorKey, quantityErrorKey, type PositionsStepErrorKey } from './positionErrorKeys';
import { useFirstPosition } from './useFirstPosition';
import { type ServiceDetails, useServiceDetails } from './useServiceDetails';

interface PositionsStepBodyProps {
    data: ApiRequisition;
    apiResourcePath: string;
    canEdit: boolean;
    /**
     * Whether the count sits beside the step title. It does in the material-only wizard, whose
     * footer has no room for it; inside the container flow the footer states it, and two counters
     * on one screen are one too many.
     */
    withCounter?: boolean;

    requestUpdateState: AsyncStateUpdate<ApiRequisition>;
}

export function PositionsStepBody({
    data,
    apiResourcePath,
    canEdit,
    withCounter = true,
    requestUpdateState,
}: PositionsStepBodyProps) {
    const i18n = useTranslate('');
    const errors = useWizardErrors<PositionsStepErrorKey>();
    const avv = useAvvCodes();
    const serviceDetails = useServiceDetails();

    const baseObjectPath = `${apiResourcePath}/${data.id}`;
    const positionsBasePath = `${baseObjectPath}/positions`;
    const { addSubObject, deleteSubObjects, requestUpdateSubObject } = useEditSubObject<
        ApiRequisitionPosition,
        ApiRequisition
    >(baseObjectPath, 'positions', requestUpdateState);

    const addPosition = useCallback(() => {
        void addSubObject();
    }, [addSubObject]);

    const deletePosition = useCallback(
        (id: string) => {
            void deleteSubObjects([id]);
        },
        [deleteSubObjects],
    );
    const count = data.positions.length;
    useFirstPosition(count, canEdit, addSubObject);

    const counterLabel = count === 1 ? `1 ${i18n('Position')}` : `${count} ${i18n('Positionen')}`;

    return (
        <>
            <WizardStepTitle
                title={i18n('Was soll abgeholt oder geliefert werden?')}
                subtitle={i18n('Pro Position wählst du Material und Menge.')}
                counter={withCounter ? counterLabel : undefined}
            />

            <FlexColumn align="stretch" gap="md">
                {data.positions.map((position, index) => (
                    <PositionCard
                        key={position.id}
                        position={position}
                        index={index}
                        positionsBasePath={positionsBasePath}
                        canEdit={canEdit}
                        canRemove={count > 1}
                        avv={avv}
                        serviceDetails={serviceDetails}
                        materialError={errors[materialErrorKey(position.id)]}
                        quantityError={errors[quantityErrorKey(position.id)]}
                        requestUpdateSubObject={requestUpdateSubObject}
                        onRemove={() => deletePosition(position.id)}
                        i18n={i18n}
                    />
                ))}

                <Button
                    appearance="dashed"
                    fullWidth
                    size="md"
                    onClick={addPosition}
                    locked={!canEdit}
                    label={i18n('Weitere Position hinzufügen')}
                >
                    <IconPlus size={iconSizeMd} />
                    {i18n('Weitere Position hinzufügen')}
                </Button>
            </FlexColumn>
        </>
    );
}

interface PositionCardProps {
    position: ApiRequisitionPosition;
    index: number;
    positionsBasePath: string;
    canEdit: boolean;
    canRemove: boolean;
    avv: AvvCodes;
    serviceDetails: ServiceDetails;
    materialError: string | undefined;
    quantityError: string | undefined;
    requestUpdateSubObject: (
        updater: Parameters<AsyncStateUpdate<ApiRequisitionPosition>>[0],
        id: string,
    ) => Awaitable<boolean>;
    onRemove: () => void;
    i18n: (key: string) => string;
}

/**
 * One position: a card with a single "Material + Menge" row, removable via the header
 * action once more than one position exists. The container picker slots in between the
 * two fields once it is in scope.
 */
function PositionCard({
    position,
    index,
    positionsBasePath,
    canEdit,
    canRemove,
    avv,
    serviceDetails,
    materialError,
    quantityError,
    requestUpdateSubObject,
    onRemove,
    i18n,
}: PositionCardProps) {
    const isPortalSession = useIsPortalSession();

    const requestUpdatePosition = useCallback<AsyncStateUpdate<ApiRequisitionPosition>>(
        (updater) => requestUpdateSubObject(updater, position.id),
        [requestUpdateSubObject, position.id],
    );

    const binder = useGuiBinder<ApiRequisitionPosition>({
        data: position,
        meta: undefined,
        canEdit,
        requestUpdateState: requestUpdatePosition,
    });
    const quantityField = useProp<ApiRequisitionPosition, number>(binder, 'quantity');

    const linkService = useLinkFieldApi<ApiRequisitionPosition, ApiService>(
        `${positionsBasePath}/${position.id}`,
        'service',
    );

    // Services seen in this card's own search results, kept only to recover the
    // full record (e.g. `decimal_places_amount`) of whichever one is selected.
    const [seenServices, setSeenServices] = useState<ReadonlyMap<string, ApiService>>(new Map());

    const { ensureCodes } = avv;
    const handleItemsChange = useCallback(
        (services: ApiService[]) => {
            ensureCodes(services.map((service) => service.id));
            setSeenServices((previous) => {
                const merged = new Map(previous);
                services.forEach((service) => merged.set(service.id, service));
                return merged;
            });
        },
        [ensureCodes],
    );

    const toMaterialItem = useCallback(
        (service: ApiService): RichSelectItemPreview => {
            const code = avv.codes.get(service.id);
            return { label: formatMaterialName(service, isPortalSession), detail: code ? `AVV ${code}` : undefined };
        },
        [avv.codes, isPortalSession],
    );

    const serviceLinked = isLinked(position.service);
    const selectedServiceId = serviceLinked ? position.service.foreign_key : undefined;

    const { ensureDetails } = serviceDetails;
    // The AVV code and full record of the currently linked service must be available even
    // if the user never (re-)opens the dropdown this session, e.g. right after resuming a draft.
    useEffect(() => {
        if (selectedServiceId) {
            ensureCodes([selectedServiceId]);
        }
    }, [selectedServiceId, ensureCodes]);

    useEffect(() => {
        if (selectedServiceId && !seenServices.has(selectedServiceId)) {
            ensureDetails([selectedServiceId]);
        }
    }, [selectedServiceId, seenServices, ensureDetails]);

    const selectedService = selectedServiceId
        ? seenServices.get(selectedServiceId) ?? serviceDetails.detailsById.get(selectedServiceId)
        : undefined;
    const selectedCode = selectedServiceId ? avv.codes.get(selectedServiceId) : undefined;
    const selectedMaterial = serviceLinked
        ? {
              label: selectedService
                  ? formatMaterialName(selectedService, isPortalSession)
                  : position.service.display_string,
              detail: selectedCode ? `AVV ${selectedCode}` : undefined,
          }
        : undefined;

    const quantityInputRef = useRef<HTMLInputElement | HTMLTextAreaElement>(undefined);

    const selectMaterial = useCallback(
        (serviceId: string) => {
            void Promise.resolve(
                requestUpdatePosition(async () => {
                    const result = await linkService.link(serviceId);
                    if (!isOk(result)) {
                        return { data: undefined, messages: result.messages };
                    }
                    return { data: result.data, messages: result.messages };
                }),
            ).then(() => {
                // Continue the flow: material picked → type the quantity right away.
                quantityInputRef.current?.focus();
            });
            return true;
        },
        [requestUpdatePosition, linkService],
    );

    // Unit and quantity only become meaningful once a material (service) is picked.
    const unit = serviceLinked ? getShortUnitLabel(position.unit) : undefined;
    // The service defines how precisely its quantity is measured.
    const quantityDecimals = selectedService?.decimal_places_amount ?? 3;

    // Wizard errors are recomputed on the next "Weiter" click only; hide them as soon as the user fixed the field.
    const showMaterialError = materialError !== undefined && !serviceLinked;
    const showQuantityError = quantityError !== undefined && !(position.quantity > 0);

    return (
        <FloatingCard
            title={`${i18n('Position')} ${index + 1}`}
            separateHeader={false}
            collapsible={false}
            additionalHeaderItems={
                canRemove && (
                    <Button
                        appearance="transparent"
                        size="xs"
                        onClick={onRemove}
                        locked={!canEdit}
                        label={i18n('Entfernen')}
                    >
                        <IconTrash size={iconSizeSm} aria-hidden="true" />
                        {i18n('Entfernen')}
                    </Button>
                )
            }
        >
            <Grid columns={12} gutter="sm" align="start" fullWidth>
                <GridColumns spans={[8, 4]}>
                    <FieldWithError error={showMaterialError ? materialError : undefined}>
                        <RichSelect<ApiService>
                            name={`material_${position.id}`}
                            label={i18n('Material / Abfallart')}
                            placeholder={i18n('Material wählen …')}
                            searchPlaceholder={i18n('Material suchen …')}
                            loadLinkableObjects={linkService.loadLinkableObjects}
                            queryKey={[positionsBasePath, position.id, 'service']}
                            toItem={toMaterialItem}
                            sortItems={sortByDisplayString}
                            onItemsChange={handleItemsChange}
                            value={selectedServiceId}
                            selected={selectedMaterial}
                            onChange={selectMaterial}
                            valid={!showMaterialError}
                            locked={!canEdit}
                        />
                    </FieldWithError>
                    <FieldWithError error={showQuantityError ? quantityError : undefined}>
                        <NumberInput
                            {...quantityField}
                            name={`quantity_${position.id}`}
                            inputRef={quantityInputRef}
                            label={i18n('Menge')}
                            placeholder={i18n('z. B. 5')}
                            suffix={unit}
                            decimals={quantityDecimals}
                            blankWhenZero
                            valid={!showQuantityError}
                        />
                    </FieldWithError>
                </GridColumns>
            </Grid>
        </FloatingCard>
    );
}
